import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

class AnalyticsTracker {
  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.pageStartTime = Date.now();
    this.currentPage = window.location.pathname;
    this.isTracking = true;
    
    // Initialize tracking
    this.init();
  }

  getOrCreateSessionId() {
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = this.generateId();
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
    return sessionId;
  }

  generateId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  init() {
    // Track page view on load
    this.trackPageView();

    // Track time spent when leaving page
    window.addEventListener('beforeunload', () => {
      this.trackTimeSpent();
    });

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.trackTimeSpent();
      } else {
        this.pageStartTime = Date.now();
      }
    });

    // Track scroll depth
    let maxScroll = 0;
    window.addEventListener('scroll', () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;
      }
    });

    // Send scroll depth before unload
    window.addEventListener('beforeunload', () => {
      if (maxScroll > 0) {
        this.trackEvent('scroll', {
          scroll_depth: maxScroll
        });
      }
    });
  }

  async trackEvent(eventType, data = {}) {
    if (!this.isTracking) return;

    try {
      const payload = {
        session_id: this.sessionId,
        event_type: eventType,
        page_path: window.location.pathname,
        page_title: document.title,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent,
        device_type: this.getDeviceType(),
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        ...data
      };

      // Use sendBeacon for beforeunload events, axios for others
      if (eventType === 'time_spent' || eventType === 'scroll') {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(`${API}/analytics/track`, blob);
      } else {
        await axios.post(`${API}/analytics/track`, payload);
      }
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
  }

  getDeviceType() {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  trackPageView() {
    this.trackEvent('page_view');
  }

  trackTimeSpent() {
    const timeSpent = Math.round((Date.now() - this.pageStartTime) / 1000);
    if (timeSpent > 0) {
      this.trackEvent('time_spent', {
        time_spent: timeSpent
      });
    }
  }

  trackClick(elementId, elementText) {
    this.trackEvent('click', {
      element_id: elementId,
      element_text: elementText
    });
  }

  trackConversion(conversionType, metadata = {}) {
    this.trackEvent('conversion', {
      conversion_type: conversionType,
      metadata: metadata
    });
  }

  // Public methods for manual tracking
  static track() {
    if (!window._analyticsTracker) {
      window._analyticsTracker = new AnalyticsTracker();
    }
    return window._analyticsTracker;
  }

  static trackConversion(type, metadata) {
    const tracker = AnalyticsTracker.track();
    tracker.trackConversion(type, metadata);
  }

  static trackClick(elementId, elementText) {
    const tracker = AnalyticsTracker.track();
    tracker.trackClick(elementId, elementText);
  }
}

// Initialize tracker
let tracker = null;

export const initAnalytics = () => {
  if (!tracker) {
    tracker = AnalyticsTracker.track();
  }
  return tracker;
};

export const trackConversion = (type, metadata) => {
  AnalyticsTracker.trackConversion(type, metadata);
};

export const trackClick = (elementId, elementText) => {
  AnalyticsTracker.trackClick(elementId, elementText);
};

export default AnalyticsTracker;
